import React, { useState, useEffect } from "react";
import {
  Card,
  List,
  Button,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  Select,
  Radio,
  Popconfirm,
  Typography,
  Tag,
  message,
  Image,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/es";

import {
  createPaymentInfo,
  updatePaymentInfo,
  deletePaymentInfo,
  uploadPaymentImage,
  fetchPaymentImages,
  deletePaymentImage,
} from "../utils/fetchPayments";
import DragAndDrop from "./DragAndDrop";
import { ThreeDot } from "react-loading-indicators";
import { useAuth } from "../contexts/AuthProvider";
import { hasPermission } from "../utils/auth";

const { Text } = Typography;

const paymentMethods = ["Efectivo", "Transferencia", "Tarjeta", "No Informado"];
const paymentStatuses = ["Pagado", "Pendiente"];

const { format } = new Intl.NumberFormat("es-CL", {
  style: "currency",
  currency: "CLP",
});

dayjs.locale("es");

const formatPaymentDate = (date) =>
  date ? dayjs(date).format("D [de] MMMM [de] YYYY") : "Sin fecha";

const deliveryLabel = (delivery) =>
  `Entrega ${formatPaymentDate(delivery.delivery_date)} - ${delivery.delivery_status}`;

const PaymentInfoCard = ({ paymentInfo, setPaymentInfo, orderID, deliveryInfo }) => {
  const { user } = useAuth();
  const canCreate = hasPermission(user?.permissions, "payments:create");
  const canUpdate = hasPermission(user?.permissions, "payments:update");
  const canDelete = hasPermission(user?.permissions, "payments:delete");

  const [loadingPaymentId, setLoadingPaymentId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [loadingModalOkButton, setLoadingModalOkButton] = useState(false);
  const [form] = Form.useForm();

  // Images keyed by payment_info_id — GET /payments/images/:paymentInfoId
  // always includes each image's id, so (unlike delivery photos) delete is
  // available for every payment image, not just ones uploaded this session.
  const [paymentImages, setPaymentImages] = useState({});
  const [loadingImagesFor, setLoadingImagesFor] = useState({});
  const [showUploadFor, setShowUploadFor] = useState(null);
  const [uploadingPayment, setUploadingPayment] = useState(null);
  const [deletingImageId, setDeletingImageId] = useState(null);

  // Workaround for ResizeObserver loop limit exceeded issue
  useEffect(() => {
    const handleResizeObserverError = () => {
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"));
      });
    };

    window.addEventListener("resize", handleResizeObserverError);

    return () => {
      window.removeEventListener("resize", handleResizeObserverError);
    };
  }, []);

  const loadImagesFor = async (paymentInfoId) => {
    setLoadingImagesFor((prev) => ({ ...prev, [paymentInfoId]: true }));
    try {
      const images = await fetchPaymentImages(paymentInfoId);
      setPaymentImages((prev) => ({ ...prev, [paymentInfoId]: images }));
    } catch (error) {
      setPaymentImages((prev) => ({ ...prev, [paymentInfoId]: [] }));
    } finally {
      setLoadingImagesFor((prev) => ({ ...prev, [paymentInfoId]: false }));
    }
  };

  useEffect(() => {
    (paymentInfo || []).forEach((item) => {
      if (item.payment_id && !(item.payment_id in paymentImages)) {
        loadImagesFor(item.payment_id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInfo]);

  const lastDeliveryId = (deliveryInfo || []).length
    ? deliveryInfo[deliveryInfo.length - 1].delivery_id
    : null;

  const handleAdd = () => {
    setEditingPayment(null);
    form.resetFields();
    form.setFieldsValue({
      payment_method: "Efectivo",
      payment_status: "Pagado",
      payment_date: dayjs(),
      delivery_info_id: lastDeliveryId,
    });
    setIsModalVisible(true);
  };

  const handleEdit = (item) => {
    setEditingPayment(item);
    form.setFieldsValue({
      ...item,
      payment_date: item.payment_date ? dayjs(item.payment_date) : null,
      delivery_info_id: item.delivery_info_id || lastDeliveryId,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (paymentId) => {
    setLoadingPaymentId(paymentId);
    try {
      await deletePaymentInfo(paymentId);
      setPaymentInfo(
        paymentInfo.filter((item) => item.payment_id !== paymentId),
      );
      setPaymentImages((prev) => {
        const next = { ...prev };
        delete next[paymentId];
        return next;
      });
      message.success("Pago eliminado correctamente.");
    } catch (error) {
      message.error("Error al eliminar el pago.");
    } finally {
      setLoadingPaymentId(null);
    }
  };

  const handleModalOk = async () => {
    setLoadingModalOkButton(true);
    try {
      const values = await form.validateFields();
      const payload = {
        payment_method: values.payment_method,
        payment_date: values.payment_date.format("YYYY-MM-DD"),
        payment_status: values.payment_status,
        amount: values.amount,
        delivery_info_id: values.delivery_info_id,
      };
      if (editingPayment) {
        // PATCH /payments/:id responds with the standalone-resource shape
        // (`id`), so drop it rather than let it collide with `payment_id`.
        const { id: _id, ...updatedFields } = await updatePaymentInfo(
          editingPayment.payment_id,
          payload,
        );
        setPaymentInfo(
          paymentInfo.map((item) =>
            item.payment_id === editingPayment.payment_id
              ? { ...item, ...updatedFields }
              : item,
          ),
        );
        message.success("Pago actualizado correctamente.");
      } else {
        const [created] = await createPaymentInfo(orderID, [payload]);
        const { id: newId, ...createdFields } = created;
        setPaymentInfo([
          ...paymentInfo,
          { ...createdFields, payment_id: newId },
        ]);
        message.success("Pago añadido correctamente.");
      }
      setIsModalVisible(false);
    } catch (err) {
      if (err?.errorFields) return; // antd form validation error, already shown inline
      message.error("Error al guardar el pago.");
    } finally {
      setLoadingModalOkButton(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handlePhotoUpload = async (files, paymentInfoId) => {
    setUploadingPayment(paymentInfoId);
    try {
      for (const file of files) {
        await uploadPaymentImage(paymentInfoId, file);
      }
      await loadImagesFor(paymentInfoId);
      message.success("Comprobante subido correctamente.");
    } catch (error) {
      message.error("Error al subir el comprobante.");
    } finally {
      setUploadingPayment(null);
      setShowUploadFor(null);
    }
  };

  const handleDeleteImage = async (paymentInfoId, imageId) => {
    setDeletingImageId(imageId);
    try {
      await deletePaymentImage(imageId);
      setPaymentImages((prev) => ({
        ...prev,
        [paymentInfoId]: (prev[paymentInfoId] || []).filter(
          (img) => img.id !== imageId,
        ),
      }));
      message.success("Comprobante eliminado correctamente.");
    } catch (error) {
      message.error("Error al eliminar el comprobante.");
    } finally {
      setDeletingImageId(null);
    }
  };

  return (
    <Card>
      <List
        itemLayout="vertical"
        dataSource={paymentInfo}
        renderItem={(item) => {
          const images = paymentImages[item.payment_id] || [];
          const isLoadingImages = !!loadingImagesFor[item.payment_id];
          return (
            <List.Item
              key={item.payment_id}
              actions={[
                canUpdate && (
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(item)}
                    disabled={loadingPaymentId === item.payment_id}
                  />
                ),
                canDelete && (
                  <Popconfirm
                    title="¿Eliminar este pago?"
                    onConfirm={() => handleDelete(item.payment_id)}
                  >
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      loading={loadingPaymentId === item.payment_id}
                      disabled={paymentInfo.length === 1}
                    />
                  </Popconfirm>
                ),
              ].filter(Boolean)}
            >
              <List.Item.Meta
                title={
                  <div className="flex items-center gap-2">
                    <span>{item.payment_method}</span>
                    <Text strong>{format(item.amount || 0)}</Text>
                    <Tag color={item.payment_status === "Pagado" ? "green" : "orange"}>
                      {item.payment_status}
                    </Tag>
                  </div>
                }
                description={
                  <>
                    {`Fecha: ${formatPaymentDate(item.payment_date)}`}
                    {(() => {
                      const delivery = (deliveryInfo || []).find(
                        (d) => d.delivery_id === item.delivery_info_id,
                      );
                      return delivery ? ` · ${deliveryLabel(delivery)}` : "";
                    })()}
                  </>
                }
              />

              <div className="mt-2">
                <div className="flex justify-between items-center mb-2">
                  <Text strong>Comprobantes</Text>
                  {canCreate && (
                    <Button
                      type="link"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        setShowUploadFor(
                          showUploadFor === item.payment_id
                            ? null
                            : item.payment_id,
                        )
                      }
                    >
                      Añadir
                    </Button>
                  )}
                </div>

                {showUploadFor === item.payment_id && (
                  <DragAndDrop
                    placeholder="Arrastra o haz clic para subir un comprobante"
                    onFiles={(files) =>
                      handlePhotoUpload(files, item.payment_id)
                    }
                  />
                )}

                {(uploadingPayment === item.payment_id || isLoadingImages) && (
                  <div className="flex justify-center py-2">
                    <ThreeDot
                      variant="brick-stack"
                      color="#4048F1"
                      size="small"
                    />
                  </div>
                )}

                {images.length > 0 ? (
                  <Image.PreviewGroup>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {images.map((img) => (
                        <div
                          key={img.id}
                          className="relative group"
                          style={{ width: 80, height: 80 }}
                        >
                          <Image
                            width={80}
                            height={80}
                            src={img.url}
                            style={{ objectFit: "cover", borderRadius: 4 }}
                          />
                          {canDelete && (
                            <Popconfirm
                              title="¿Eliminar este comprobante?"
                              onConfirm={() =>
                                handleDeleteImage(item.payment_id, img.id)
                              }
                            >
                              <Button
                                danger
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                loading={deletingImageId === img.id}
                                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                style={{
                                  zIndex: 10,
                                  background: "rgba(255,255,255,0.85)",
                                  minWidth: 0,
                                  padding: "0 4px",
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Popconfirm>
                          )}
                        </div>
                      ))}
                    </div>
                  </Image.PreviewGroup>
                ) : (
                  !isLoadingImages &&
                  uploadingPayment !== item.payment_id && (
                    <Text type="secondary">
                      <span className="italic text-sm">Sin comprobantes</span>
                    </Text>
                  )
                )}
              </div>
            </List.Item>
          );
        }}
      />
      {canCreate && (
        <Button
          type="dashed"
          onClick={handleAdd}
          icon={<PlusOutlined />}
          style={{ width: "100%", marginTop: 16 }}
        >
          Añadir Pago
        </Button>
      )}
      <Modal
        title={editingPayment ? "Editar Pago" : "Añadir Pago"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={editingPayment ? "Guardar" : "Añadir"}
        confirmLoading={loadingModalOkButton}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="payment_method"
            label="Método de Pago"
            rules={[
              { required: true, message: "Debes seleccionar un método de pago!" },
            ]}
          >
            <Select>
              {paymentMethods.map((method) => (
                <Select.Option key={method} value={method}>
                  {method}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="payment_date"
            label="Fecha de Pago"
            rules={[
              { required: true, message: "Debes ingresar una fecha de pago!" },
            ]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="amount"
            label="Monto"
            rules={[{ required: true, message: "Debes ingresar un monto!" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="payment_status"
            label="Estado de Pago"
            rules={[
              { required: true, message: "Debes seleccionar un estado de pago!" },
            ]}
          >
            <Radio.Group buttonStyle="solid" style={{ display: "flex" }}>
              {paymentStatuses.map((status) => (
                <Radio.Button
                  key={status}
                  value={status}
                  style={{ flex: 1, textAlign: "center" }}
                >
                  {status}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="delivery_info_id"
            label="Entrega Asociada"
            rules={[
              { required: true, message: "Debes seleccionar una entrega!" },
            ]}
          >
            <Select>
              {(deliveryInfo || []).map((delivery) => (
                <Select.Option
                  key={delivery.delivery_id}
                  value={delivery.delivery_id}
                >
                  {deliveryLabel(delivery)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default PaymentInfoCard;
